import _merge from "lodash/merge";
import _mergeWith from "lodash/mergeWith";
import _isArray from "lodash/isArray";
import { v4 } from "uuid";

import {
  formatUser,
  formatSpace,
  formatCharge,
  formatApiToken,
  formatSpaceMember,
  formatKit,
  formatKitMember,
  formatKitVersion,
  formatSection,
  formatItem,
  formatAsset,
  formatInvitation,
  formatSpacePreview,
  formatOutlineHeader,
  formatPortal,
} from "../redux/entityFormatters";
import { AssetType, ItemType } from "../constants/types";
import { AnyObject, Item, Kit, Section, Space } from "../models";
import { autoNormalize } from "../redux/autoNormalize";

function genUUID(): string {
  return v4();
}

function genShortId(): string {
  return "xxxxxxxx".replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0,
      v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function genID(): number {
  return Math.round(Math.random() * 10000) + 1;
}

function genEmail(): string {
  const name = genName().replace(" ", "_");
  return `${name}@lingoapp.com`;
}

/**
 * Generates a alpha string
 * @param {*} length
 */
function genString(length = 10): string {
  const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz",
    charactersLength = characters.length;
  return Array(length)
    .fill(null)
    .map(() => characters.charAt(Math.floor(Math.random() * charactersLength)))
    .join("");
}

function genName(): string {
  const words = [
    "ants",
    "excited",
    "press",
    "spotty",
    "coat",
    "gato",
    "bathe",
    "heat",
    "deeply",
    "embarrass",
    "mist",
    "craven",
    "neighborly",
    "funny",
    "man",
    "van",
    "tranquil",
    "grandiose",
    "march",
    "road",
  ];
  const wordCount = words.length;
  const numWords = 2 + Math.floor(Math.random() * 2);
  const res = [];
  for (let i = 0; i < numWords; i += 1) {
    const w = words[Math.floor(Math.random() * wordCount)];
    res.push(w);
  }
  return res.join(" ");
}

// MARK : Default IDs
/*-------------------------------------------------------------------------------*/
export const portalUUID = genUUID();
export const kitUUID = genUUID();
export const sectionUUID = genUUID();
export const spaceID = genID();
export const userID = genID();

// MARK : Generators
/*-------------------------------------------------------------------------------*/

function mergeRules(objValue, srcValue) {
  if (_isArray(objValue)) {
    return srcValue || objValue;
  }
}

function createUser(overrides = {}) {
  return {
    __type: "user",
    id: userID,
    name: "Jordan Smith",
    email: `jsmith@thenounproject.com`,
    app_progress: "onboard_v1,app_login,download,example_kit,created_in_app",
    avatar: "https://static-test.lingoapp.com/avatar/3efacf39-cc23-41e4-afc2-9fb2631c5563.png",
    features: [],
    installed_app: "2016-01-05 22:06:22.942431+0000",
    is_active: true,
    is_admin: true,
    last_login: "2018-12-13 22:44:15.454399+0000",
    created_at: "2016-01-12 19:46:59.683120+0000",
    updated_at: "2018-12-13 20:43:47.785993+0000",
    ...overrides,
  };
}

function createNestedUser(overrides = {}) {
  return {
    __type: "user",
    avatar:
      "https://www.gravatar.com/avatar/64fe995229067daf8862575df759b43a?s=240&d=https%3A%2F%2Fstatic.lingoapp.com%2Fassets%2Fimages%2Fuser-avatars%2Fv2-12.png",
    email: "jSmith@thenounproject.com",
    id: userID,
    name: "Jordan Smith",
    ...overrides,
  };
}

function createSpace(overrides: AnyObject = {}) {
  return _mergeWith(
    {
      __type: "space",
      access: {
        date_added: "2016-01-19 20:29:34.669786+0000",
        date_updated: "2016-01-19 20:29:34.669786+0000",
        id: genID(),
        permissions: [
          "delete",
          "create_kit",
          "edit",
          "invite_user",
          "change_user_role",
          "revoke_invitation",
          "remove_user",
          "manage_billing",
          "manage_resources",
          "view_members",
          "create_asset",
          "view_library",
          "manage_tags",
          "view_fields",
          "edit_fields",
        ],
        upgrade_permissions: {},
        role: "owner",
        space_id: overrides.id || spaceID,
        status: "active",
        user_id: userID,
      },
      avatar:
        "https://static-test.lingoapp.com/avatar/s/72/8C3F847C-DFCE-4B61-9D8C-50FF1A5B9DFF.png",
      avatars: createSpaceAvatars(),
      counts: createSpaceCounts(),
      date_added: "2016-01-19 20:29:34.669786+0000",
      date_updated: "2018-11-16 20:54:13.815200+0000",
      features: ["portals", "space_theme", "web_edit_content", "kit_versioning"],
      id: spaceID,
      max_assets: null,
      max_users: null,
      name: "Guest Mania",
      slug: "guest-mania",
      status: "active",
      privacy: "private",
      join_settings: {
        token: null,
        join_with: {},
        allowed_domains: null,
      },
      subscription: {
        cancel_at_period_end: false,
        current_period_end: "2016-11-26 00:04:05+00:00",
        customer: {
          coupon: null,
          stripe_id: "cus_8KzfYdDX0a6WGE",
        },
        interval: "month",
        plan: "tier-enterprise-month",
        price: 4900,
        price_per_user: null,
        quantity: 1,
        status: "active",
        stripe_id: "sub_8KzffcSRwSnAm6",
        total_price: null,
      },
      theme: createSpaceTheme(),
      uuid: "team_18",
    },
    overrides,
    mergeRules
  );
}

function createSpaceAvatars(overrides = {}) {
  return {
    avatar: "https://static-test.lingoapp.com/avatar/s/72/8C3F847C-DFCE-4B61-9D8C-50FF1A5B9DFF.png",
    avatar_16:
      "https://static-test.lingoapp.com/avatar/s/72/8C3F847C-DFCE-4B61-9D8C-50FF1A5B9DFF_16.png",
    avatar_180:
      "https://static-test.lingoapp.com/avatar/s/72/8C3F847C-DFCE-4B61-9D8C-50FF1A5B9DFF_180.png",
    avatar_32:
      "https://static-test.lingoapp.com/avatar/s/72/8C3F847C-DFCE-4B61-9D8C-50FF1A5B9DFF_32.png",
    card: "https://static-test.lingoapp.com/avatar/s/72/8C3F847C-DFCE-4B61-9D8C-50FF1A5B9DFF_card.png",
    default_avatar: "https://static-test.lingoapp.com/assets/images/user-avatars/space.png",
    ...overrides,
  };
}

function createSpaceCounts(overrides = {}) {
  return {
    assets: 20,
    invitations: 16,
    storage: 958991,
    users: 376,
    ...overrides,
  };
}

function createSpaceTheme(overrides = {}) {
  return {
    primary_color: "#AAAAAA",
    primary_color_dark: "#555555",
    primary_color_light: "#BBBBBB",
    primary_color_tint: "#FFFFFF",
    ...overrides,
  };
}

function createSpacePreview(overrides = {}) {
  return {
    id: spaceID,
    name: genName(),
    subdomain: null,
    avatar: "https://static-test.lingoapp.com/avatar/s/72/8C3F847C-DFCE-4B61-9D8C-50FF1A5B9DFF.png",
    avatars: createSpaceAvatars(),
    theme: createSpaceTheme(),
    ...overrides,
  };
}

function createSpacePreviewMember(overrides = {}) {
  return {
    id: genID(),
    name: genName(),
    email: genEmail(),
    avatar:
      "https://www.gravatar.com/avatar/64fe995229067daf8862575df759b43a?s=240&d=https%3A%2F%2Fstatic.lingoapp.com%2Fassets%2Fimages%2Fuser-avatars%2Fv2-12.png",
    role: "admin",
    ...overrides,
  };
}

function createSpaceCard(overrides: AnyObject = {}) {
  const stripeId = `ch_${genShortId()}`;
  return {
    brand: "Visa",
    country: "US",
    exp_month: 2,
    exp_year: 2024,
    last4: "4242",
    receipt_extra: null,
    stripe_id: stripeId,
    ...overrides,
  };
}
function createSpaceCharge(overrides: AnyObject = {}) {
  const stripeId = overrides.stripe_id || `ch_${genShortId()}`;
  return {
    __type: "charge",
    id: genID(),
    amount: 165.31,
    card: "Visa 4242",
    date: "2016-11-03 23:47:26.421327+0000",
    invoice_url: `/v4/invoices/${stripeId}`,
    receipt_extra: null,
    refunded: false,
    status: "succeeded",
    stripe_id: stripeId,
    ...overrides,
  };
}

function createApiToken(overrides = {}) {
  return {
    __type: "api_token",
    token: genUUID(),
    name: "My Api Token",
    writeAccess: false,
    status: "active",
    space_id: spaceID,
    created_at: "2016-11-03 23:47:26.421327+0000",
    ...overrides,
  };
}

function createSpaceMember(overrides: AnyObject = {}) {
  const userId = overrides.user_id || genID();
  return _mergeWith(
    {
      __type: "space_access",
      id: genID(),
      role: "member",
      space_id: spaceID,
      status: "active",
      joined_kits: 0,
      permissions: ["search_assets", "view_members", "remove_self"],
      user_id: userId,
      user: createNestedUser({ id: userId }),
      date_added: "2016-11-03 23:47:26.421327+0000",
      date_updated: "2017-06-15 16:19:48.204267+0000",
    },
    overrides,
    mergeRules
  );
}

function createInvitation(overrides = {}) {
  return _mergeWith(
    {
      __type: "invitation",
      id: genID(),
      email: `${genName().split(" ").join("_")}@lingoapp.com`,
      space_id: spaceID,
      join_with: {
        role: "admin",
        kits: [],
      },
      note: "",
      token: genUUID(),
      invited_at: "2017-06-15 16:19:48.204267+0000",
      accepted_at: null,
      status: "pending",
      from_user_id: genID(),
    },
    overrides,
    mergeRules
  );
}

function createPortal(overrides: AnyObject = {}) {
  const _spaceID = overrides.space_id || spaceID,
    _portalID = overrides.portal_uuid || portalUUID;

  return _mergeWith(
    {
      __type: "portal",
      access: {
        date_added: "2018-03-12 04:59:16.472477+0000",
        date_updated: "2018-03-12 04:59:16.472477+0000",
        id: genID(),
        permissions: ["view", "edit", "delete", "change_member_role", "manage_sharelinks"],
        upgrade_permissions: {},
        role: "admin",
        space_id: _spaceID,
        status: "active",
        use_version: null,
        user_id: 2,
      },
      id: _portalID,
      shortId: genShortId(),
      spaceId: _spaceID,
      name: genName(),
      description: "Best portal ever",
      status: "active",
      theme: null,
      counts: {
        kits: 1,
        members: 1,
        items: 1,
      },
      images: {
        cover:
          "https://lingo-test.s3.amazonaws.com/kits/factory.spaceID/216F2D9A-68C0-4F4E-B728-DB0042A97791/cover_1524242220.png",
      },
      privacy: "private",
      indexable: false,
      downloadsRestricted: false,
      dateAdded: "2024-05-12 04:59:16.472477+0000",
      dateUpdated: "2024-05-12 04:59:16.472477+0000",
    },
    overrides,
    mergeRules
  );
}

function createKit(overrides: AnyObject = {}) {
  const _spaceID = overrides.space_id || spaceID,
    _kitID = overrides.kit_uuid || kitUUID;

  return _mergeWith(
    {
      __type: "kit",
      access: {
        date_added: "2018-03-12 04:59:16.472477+0000",
        date_updated: "2018-03-12 04:59:16.472477+0000",
        id: 7929,
        is_favorite: false,
        kit_uuid: _kitID,
        permissions: [
          "manage",
          "view",
          "edit",
          "delete",
          "change_member_role",
          "manage_sharelinks",
          "change_own_status",
          "edit_content",
          "search_assets",
          "view_all_content",
          "favorite",
        ],
        upgrade_permissions: {},
        role: "admin",
        space_id: _spaceID,
        status: "active",
        use_version: null,
        user_id: 2,
      },
      counts: { invitations: 0, members: 6 },
      date_added: "2018-03-12 04:59:16.472477+0000",
      date_updated: "2018-04-20 16:37:00.807095+0000",
      id: 2474,
      images: {
        cover:
          "https://lingo-test.s3.amazonaws.com/kits/factory.spaceID/216F2D9A-68C0-4F4E-B728-DB0042A97791/cover_1524242220.png",
      },
      privacy: "private",
      portal_access: false,
      kit_uuid: _kitID,
      short_id: genShortId(),
      name: genName(),
      notes: "",
      space_id: _spaceID,
      status: "active",
      token: null,
      use_version: 0,
    },
    overrides,
    mergeRules
  );
}

function createKitMember(overrides: AnyObject = {}) {
  const userId = overrides.user_id || genID();
  return _mergeWith(
    {
      __type: "kit_access",
      id: genID(),
      role: "admin",
      space_id: spaceID,
      kit_uuid: kitUUID,
      user_id: userId,
      status: "active",
      is_favorite: false,
      use_version: 0,
      permissions: [
        "manage",
        "view",
        "edit",
        "delete",
        "change_member_role",
        "manage_sharelinks",
        "change_own_status",
        "edit_content",
        "search_assets",
        "view_all_content",
      ],
      user: createNestedUser({ id: userId }),
      date_added: "2016-11-03 23:47:26.421327+0000",
      date_updated: "2017-06-15 16:19:48.204267+0000",
    },
    overrides,
    mergeRules
  );
}

function createVersion(overrides = {}) {
  return _merge(
    {
      __type: "version",
      counts: { assets: 0, items: 0, sections: 0 },
      date_added: "2018-03-12 04:59:16.472477+0000",
      date_updated: "2018-03-12 04:59:16.472477+0000",
      kit_uuid: kitUUID,
      notes: "",
      space_id: spaceID,
      status: "active",
      store_status: "inactive",
      version: 0,
      version_identifier: "0.0",
      sections: [],
    },
    overrides
  );
}

function createOutlineSection(overrides = {}) {
  return _merge(
    {
      __type: "section",
      uuid: genUUID(),
      short_id: genShortId(),
      name: genName(),
      display_order: 0,
      headers: [],
      counts: { assets: 0, items: 0 },
      version: 0,
    },
    overrides
  );
}

function createOutlineHeader(overrides = {}) {
  return {
    uuid: genUUID(),
    short_id: genShortId(),
    name: genName(),
    version: 0,
    display_order: 0,
    ...overrides,
  };
}

function createSection(overrides = {}) {
  return _merge(
    {
      __type: "section",
      uuid: genUUID(),
      short_id: genShortId(),
      kit_uuid: kitUUID,
      name: genName(),
      counts: { assets: 1, items: 1 },
      display_order: 0,
      status: "active",
      version: 0,
      creator_id: 2,
      date_added: "2018-03-12 04:59:22.760599+0000",
      date_updated: "2018-03-12 04:59:22.760599+0000",
    },
    overrides
  );
}

function createItem(overrides = {}) {
  return _merge(
    {
      __type: "item",
      asset_uuid: null,
      data: {} as AnyObject,
      date_added: "2018-04-05 20:13:53.528112+0000",
      date_updated: "2018-04-05 20:13:53.528121+0000",
      display_order: -20,
      status: "active",
      type: ItemType.heading,
      uuid: genUUID(),
      version: 0,
      short_id: genShortId(),
      space_id: spaceID,
      kit_uuid: kitUUID,
      section_uuid: sectionUUID,
    },
    overrides
  );
}

function createHeadingItem(overrides = {}) {
  return createItem({
    ..._merge(
      {
        data: {
          content: "Stars & Nebulas",
          display_size: "0",
        },
      },
      overrides
    ),
    asset_uuid: null,
    type: ItemType.heading,
  });
}

function createNoteItem(overrides = {}) {
  return createItem({
    ..._merge(
      {
        data: {
          content: "These photos provided by NASA.",
          display_size: "0",
        },
      },
      overrides
    ),
    asset_uuid: null,
    type: ItemType.note,
  });
}

function createGuideItem(asset: AnyObject = null, overrides = {}) {
  return createItem({
    ..._merge(
      {
        data: {
          content: "Make sure you do this",
          color: "green",
          title: "DO",
          display_style: "image",
          display_size: 1,
        },
      },
      overrides
    ),
    type: ItemType.guide,
    asset_uuid: asset ? asset.uuid : null,
    space_id: asset ? asset.space_id : spaceID,
    asset: asset,
  });
}

function createGalleryItem(asset: AnyObject = null, overrides = {}) {
  return createItem({
    ..._merge(
      {
        data: {
          view_id: "Make sure you do this",
          view_name: "green",
          assets: 0,
        },
      },
      overrides
    ),
    type: ItemType.gallery,
    asset_uuid: asset ? asset.uuid : null,
    space_id: asset ? asset.space_id : spaceID,
    asset: asset,
  });
}

function createCodeSnippetItem(overrides = {}) {
  return createItem({
    ..._merge(
      {
        data: {
          content: 'const name = "Bob"',
          display_size: "0",
          code_language: "JavaScript",
        },
      },
      overrides
    ),
    asset_uuid: null,
    type: ItemType.codeSnippet,
  });
}

function createAssetItem(asset: AnyObject, overrides = {}) {
  return createItem({
    ...overrides,
    type: ItemType.asset,
    asset_uuid: asset.uuid,
    space_id: asset.space_id,
    asset: asset,
  });
}

function createImageItem(overrides = {}) {
  return createAssetItem(createImageAsset(), overrides);
}

function createVideoItem(overrides = {}) {
  return createAssetItem(createVideoAsset(), overrides);
}

function createLottieItem(overrides = {}) {
  return createAssetItem(createLottieAsset(), overrides);
}

function createAudioItem(overrides = {}) {
  return createAssetItem(createAudioAsset(), overrides);
}

function createColorItem(overrides = {}) {
  return createAssetItem(createColorAsset(), overrides);
}

function createFontItem(overrides = {}) {
  return createAssetItem(createFontAsset(), overrides);
}

function createSupportingImageItem(overrides = {}) {
  const item = createAssetItem(createImageAsset(), overrides);
  item.type = ItemType.supportingImage;
  return item;
}
function createSupportingVideoItem(overrides = {}) {
  const item = createAssetItem(createVideoAsset(), overrides);
  item.type = ItemType.supportingImage;
  return item;
}
function createSupportingLottieItem(overrides = {}) {
  const item = createAssetItem(createLottieAsset(), overrides);
  item.type = ItemType.supportingImage;
  return item;
}

const thumbnails = {
  thumbnail_placeholders: {
    292: "https://lingo-test.s3.amazonaws.com/thumbnails/placeholder/svg/292x292.png",
    480: "https://lingo-test.s3.amazonaws.com/thumbnails/placeholder/svg/480x480.png",
    1232: "https://lingo-test.s3.amazonaws.com/thumbnails/placeholder/svg/1232x1232.png",
  },
  thumbnails: {
    292: "https://api-test.lingoapp.com/v4/assets/AFCD9898-BB8B-455C-8429-5D4717496A33/preview?size=292&asset_token=G3Ss23q1ARG8z3zrsHDCssEhM-B9lkZZsYIyEUMgukY",
    480: "https://api-test.lingoapp.com/v4/assets/AFCD9898-BB8B-455C-8429-5D4717496A33/preview?size=480&asset_token=G3Ss23q1ARG8z3zrsHDCssEhM-B9lkZZsYIyEUMgukY",
    1232: "https://api-test.lingoapp.com/v4/assets/AFCD9898-BB8B-455C-8429-5D4717496A33/preview?size=1232&asset_token=G3Ss23q1ARG8z3zrsHDCssEhM-B9lkZZsYIyEUMgukY",
  },
};

function createImageAsset(overrides = {}) {
  return {
    __type: "asset",
    category: "raster",
    colors: [],
    date_added: "2018-03-12 04:59:44.891239+0000",
    date_updated: "2018-12-14 15:49:21.701801+0000",
    dimensions: "100.0x100.0",
    file_hash: "9622e706196bf2dd6db976c10cdb2c4c97a4673c",
    file_id: "4D421CC8-353C-49F6-9538-124A924A76B2",
    file_updated: "2018-03-12 04:59:44.891239+0000",
    keywords: "Kaiser,antimodernization,pretraveling,tubercularise",
    meta: {
      assetProcessing: "complete",
      filecuts: {
        available_types: [
          {
            enabled: true,
            resizable: true,
            type: "PNG",
          },
          {
            enabled: true,
            resizable: true,
            type: "JPG",
          },
        ],
        presets: [
          {
            description: "",
            size: "1x",
            type: "PNG",
          },
          {
            description: "",
            size: "1x",
            type: "JPG",
          },
        ],
      },
    },
    name: genName(),
    notes: "",
    permalink:
      "https://api-test.lingoapp.com/v4/assets/AFCD9898-BB8B-455C-8429-5D4717496A33/download?asset_token=G3Ss23q1ARG8z3zrsHDCssEhM-B9lkZZsYIyEUMgukY",
    size: 16326,
    space_id: spaceID,
    token: "G3Ss23q1ARG8z3zrsHDCssEhM-B9lkZZsYIyEUMgukY",
    type: AssetType.png,
    uploader_id: 2,
    uuid: genUUID(),
    ...thumbnails,
    ...overrides,
  };
}

function createLottieAsset(overrides = {}) {
  return {
    __type: "asset",
    category: "video",
    colors: [],
    date_added: "2018-03-12 04:59:44.891239+0000",
    date_updated: "2018-12-14 15:49:21.701801+0000",
    dimensions: "100.0x100.0",
    file_hash: "9622e706196bf2dd6db976c10cdb2c4c97a4673c",
    file_id: "4D421CC8-353C-49F6-9538-124A924A76B2",
    file_updated: "2018-03-12 04:59:44.891239+0000",
    keywords: "Kaiser,antimodernization,pretraveling,tubercularise",
    name: genName(),
    notes: "",
    permalink:
      "https://api-test.lingoapp.com/v4/assets/AFCD9898-BB8B-455C-8429-5D4717496A33/download?asset_token=G3Ss23q1ARG8z3zrsHDCssEhM-B9lkZZsYIyEUMgukY",
    size: 16326,
    space_id: spaceID,
    token: "G3Ss23q1ARG8z3zrsHDCssEhM-B9lkZZsYIyEUMgukY",
    type: AssetType.lottie,
    uploader_id: 2,
    uuid: genUUID(),
    meta: {
      assetProcessing: "complete",
    },
    ...thumbnails,
    ...overrides,
  };
}

function createVideoAsset(overrides = {}) {
  return _merge(
    {
      __type: "asset",
      category: "video",
      colors: [],
      date_added: "2018-03-12 04:59:44.891239+0000",
      date_updated: "2018-12-14 15:49:21.701801+0000",
      dimensions: "100.0x100.0",
      file_hash: "9622e706196bf2dd6db976c10cdb2c4c97a4673c",
      file_id: "4D421CC8-353C-49F6-9538-124A924A76B2",
      file_updated: "2018-03-12 04:59:44.891239+0000",
      keywords: "Kaiser,antimodernization,pretraveling,tubercularise",
      name: genName(),
      notes: "",
      permalink:
        "https://api-test.lingoapp.com/v4/assets/AFCD9898-BB8B-455C-8429-5D4717496A33/download?asset_token=G3Ss23q1ARG8z3zrsHDCssEhM-B9lkZZsYIyEUMgukY",
      size: 16326,
      space_id: spaceID,
      token: "G3Ss23q1ARG8z3zrsHDCssEhM-B9lkZZsYIyEUMgukY",
      type: AssetType.mp4,
      uploader_id: 2,
      uuid: genUUID(),
      meta: {
        assetProcessing: "complete",
        duration: 30,
      },
      ...thumbnails,
    },
    overrides
  );
}

function createAudioAsset(overrides = {}) {
  return {
    __type: "asset",
    category: "audio",
    colors: [],
    date_added: "2018-03-12 04:59:44.891239+0000",
    date_updated: "2018-12-14 15:49:21.701801+0000",
    dimensions: "0x0",
    file_hash: "9622e706196bf2dd6db976c10cdb2c4c97a4673c",
    file_id: "4D421CC8-353C-49F6-9538-124A924A76B2",
    file_updated: "2018-03-12 04:59:44.891239+0000",
    keywords: "Kaiser,antimodernization,pretraveling,tubercularise",
    name: genName(),
    notes: "",
    permalink:
      "https://api-test.lingoapp.com/v4/assets/AFCD9898-BB8B-455C-8429-5D4717496A33/download?asset_token=G3Ss23q1ARG8z3zrsHDCssEhM-B9lkZZsYIyEUMgukY",
    size: 16326,
    space_id: spaceID,
    token: "G3Ss23q1ARG8z3zrsHDCssEhM-B9lkZZsYIyEUMgukY",
    type: AssetType.mp3,
    uploader_id: 2,
    uuid: genUUID(),
    meta: {
      assetProcessing: "complete",
      duration: 1.175,
    },
    ...thumbnails,
    ...overrides,
  };
}

function createColorAsset(overrides = {}) {
  return {
    __type: "asset",
    category: "color",
    colors: [
      {
        alpha: 100,
        brightness: 81.96078,
        coverage: 100,
        hue: 360,
        name: "Candy",
        saturation: 100,
      },
    ],
    date_added: "2018-07-16 19:26:22.771418+0000",
    date_updated: "2018-08-31 15:51:52.472919+0000",
    dimensions: "0x0",
    file_hash: "8e7e4c3c4c16c5422116153ae300160901186e9d",
    file_id: null,
    file_updated: "2018-07-16 19:26:22.771418+0000",
    keywords: "red",
    meta: { external_id: genUUID() },
    name: genName(),
    notes: "red",
    size: 0,
    space_id: spaceID,
    token: "zo8YcVI-Ih0-Wbk4bQrgjNczhZMyNpIQH7kfvzDMskw",
    type: AssetType.color,
    uploader_id: 2,
    uuid: genUUID(),
    ...overrides,
  };
}

function createLinkAsset(overrides = {}) {
  return {
    __type: "asset",
    category: "link",
    date_added: "2018-07-16 19:26:22.771418+0000",
    date_updated: "2018-08-31 15:51:52.472919+0000",
    dimensions: "0x0",
    file_hash: "8e7e4c3c4c16c5422116153ae300160901186e9d",
    file_id: null,
    file_updated: "2018-07-16 19:26:22.771418+0000",
    meta: {
      content: {
        url: "https://www.lingoapp.com/",
        description:
          "Lingo is the visual home for your brand. It is designed to help teams organize, manage and share their assets in one place.",
        title: "Lingo App",
        site_name: "Lingo",
        favicon: "https://lingo-test.s3.amazonaws.com/340a0dc8-d415-4c9e-8966-b712bb1f3d93.png",
        fld: "lingoapp.com",
      },
    },
    name: "Lingo App",
    size: 0,
    space_id: spaceID,
    token: "zo8YcVI-Ih0-Wbk4bQrgjNczhZMyNpIQH7kfvzDMskw",
    type: AssetType.URL,
    uploader_id: 2,
    uuid: genUUID(),
    ...overrides,
  };
}

function createFontAsset(overrides = {}) {
  return {
    __type: "asset",
    category: "text_style",
    colors: [],
    date_added: "2018-10-02 18:52:29.586325+0000",
    date_updated: "2018-10-24 19:18:01.357386+0000",
    dimensions: "0.0x0.0",
    file_hash: "5cab77a2faac5b6bdbdb22808f8e9296100b1529",
    file_id: "e86cc57a-2dfb-47e8-befb-afcc4243b065",
    file_updated: "2018-10-02 18:52:29.586325+0000",
    keywords: "font,heading,big,bold,sans-serif",
    meta: {
      font: {
        display_name: "Input Bold",
        extension: "ttf",
        family: "Input",
        font_name: "Input-Bold",
        source: "file",
        stylesheet_url:
          "https://api-test.lingoapp.com/v4/assets/8424E3C7-4AF5-46D4-8BD1-C4363BF78CF6/css?asset_token=_Zhe87o4B2NFIoNaX5sagApadSxcLlzHD9-BLiHcVNY",
        variant: "Bold",
      },
    },
    name: "Heading",
    notes: "Use this for all the headings",
    permalink:
      "https://api-test.lingoapp.com/v4/assets/8424E3C7-4AF5-46D4-8BD1-C4363BF78CF6/download?asset_token=_Zhe87o4B2NFIoNaX5sagApadSxcLlzHD9-BLiHcVNY",
    size: 122340,
    space_id: spaceID,
    token: "_Zhe87o4B2NFIoNaX5sagApadSxcLlzHD9-BLiHcVNY",
    type: AssetType.textStyle,
    uploader_id: 2,
    uuid: genUUID(),
    ...thumbnails,
    ...overrides,
  };
}

function createDocumentAsset(overrides = {}) {
  return {
    __type: "asset",
    category: "document",
    colors: [],
    date_added: "2018-10-02 18:52:29.586325+0000",
    date_updated: "2018-10-24 19:18:01.357386+0000",
    dimensions: "0.0x0.0",
    file_hash: "5cab77a2faac5b6bdbdb22808f8e9296100b1529",
    file_id: "e86cc57a-2dfb-47e8-befb-afcc4243b065",
    file_updated: "2018-10-02 18:52:29.586325+0000",
    keywords: "file,presentation,keynote",
    meta: {},
    name: "Presentation",
    notes: "Use this for all the presentations",
    permalink:
      "https://api-test.lingoapp.com/v4/assets/8424E3C7-4AF5-46D4-8BD1-C4363BF78CF6/download?asset_token=_Zhe87o4B2NFIoNaX5sagApadSxcLlzHD9-BLiHcVNY",
    size: 122340,
    space_id: spaceID,
    token: "_Zhe87o4B2NFIoNaX5sagApadSxcLlzHD9-BLiHcVNY",
    type: AssetType.pptx,
    uploader_id: 2,
    uuid: genUUID(),
    ...thumbnails,
    ...overrides,
  };
}

function createSketchAsset(overrides = {}) {
  return _mergeWith(
    {
      __type: "asset",
      category: "sketch",
      colors: [],
      date_added: "2018-03-12 05:01:26.570207+0000",
      date_updated: "2018-03-12 05:01:26.570713+0000",
      dimensions: "320.0x200.0",
      file_hash: "9a427051418fbfaa14c30cbd9b95ecd58cc0f86c",
      file_id: "EDCB69C7-6BB4-4677-8A73-4BDB72C2BD2D",
      file_updated: "2018-03-12 05:01:26.570207+0000",
      keywords: "Image",
      meta: {
        embedded_cuts: "SVG,PDF",
        external_hash: "3dc6e4a666b4b1a2d7762d074457ef0528e6464a",
        sketch_object_id: genUUID(),
        sketch_format_version: "101_2",
        sketch_pathname: "Image / 16 : 1",
        filecuts: {
          available_types: [
            {
              enabled: true,
              resizable: true,
              type: "PNG",
            },
            {
              enabled: true,
              resizable: true,
              type: "JPG",
            },
            {
              enabled: true,
              resizable: false,
              type: "SVG",
            },
          ],
          presets: [
            {
              description: "",
              size: "1x",
              type: "SVG",
            },
            {
              description: "",
              size: "1x",
              type: "PNG",
            },
            {
              description: "",
              size: "1x",
              type: "JPG",
            },
          ],
        },
      },
      name: genName(),
      notes: "",
      permalink:
        "http://local.lingoapp.com:9000/v4/assets/A4508D1F-51E4-477D-A747-F2FC3CB903B5/download?asset_token=C4kl9Nba7X44Ha54wAn1B40hf1B0B93iOZxo9_KGOY8",
      size: 15681,
      space_id: spaceID,
      token: "C4kl9Nba7X44Ha54wAn1B40hf1B0B93iOZxo9_KGOY8",
      uploader_id: 2,
      type: AssetType.sketchSymbol,
      uuid: genUUID(),
      ...thumbnails,
    },
    overrides,
    mergeRules
  );
}

// MARK : Normalized/State data
/*-------------------------------------------------------------------------------*/

/**
 * Easily normalize factory objects as they would be in state.
 *
 * ex. `factory.normalize.space(factory.createSpace())`
 *
 * Note that the overrides use with the factory object creators may be different
 * than the keys you are expecting from the normalized version. Overrides should
 * be supplied as they would come from the API.
 */

type StateObjects = {
  spaces?: AnyObject[];
  kits?: AnyObject[];
  sections?: AnyObject[];
  items?: AnyObject[];
};
const createState = function (entities: StateObjects) {
  const { spaces = [], kits = [], sections = [], items = [] } = entities;
  const res = autoNormalize({
    spaces,
    kits,
    sections,
    items,
  });
  const migratedEntities = ["kits", "items", "spaces", "sections"];
  migratedEntities.forEach(key => {
    res.entities[key] = { objects: res.entities[key] };
  });
  return res as {
    result: { spaces: number[]; kits: string[]; sections: string[]; items: string[] };
    entities: {
      spaces: { objects: Record<number, Space> };
      kits: { objects: Record<string, Kit> };
      sections: { objects: Record<string, Section> };
      items: { objects: Record<string, Item> };
    };
  };
};

const normalize = {
  user: (data: AnyObject) => formatUser(data),
  space: (data: AnyObject) => formatSpace(data),
  spacePreview: (data: AnyObject) => formatSpacePreview(data),
  charge: (data: AnyObject) => formatCharge(data),
  apiToken: (data: AnyObject) => formatApiToken(data),
  spaceMember: (data: AnyObject) => formatSpaceMember(data),
  invitation: (data: AnyObject) => formatInvitation(data),
  kit: (data: AnyObject) => formatKit(data),
  kitMember: (data: AnyObject) => formatKitMember(data),
  kitVersion: (data: AnyObject) => formatKitVersion(data),
  section: (data: AnyObject) => formatSection(data),
  item: (data: AnyObject) => formatItem(data),
  asset: (data: AnyObject) => formatAsset(data),
  outlineHeader: (data: AnyObject) => formatOutlineHeader(data),
  portal: (data: AnyObject) => formatPortal(data),
};

export default {
  normalize,
  genID,
  genUUID,
  genString,
  createUser,
  createNestedUser,
  createSpace,
  createSpacePreview,
  createSpacePreviewMember,
  createSpaceTheme,
  createSpaceCounts,
  createSpaceCard,
  createSpaceCharge,
  createInvitation,
  createApiToken,
  createSpaceMember,
  createKit,
  createKitMember,
  createVersion,
  createOutlineHeader,
  createOutlineSection,
  createSection,
  createHeadingItem,
  createNoteItem,
  createGuideItem,
  createGalleryItem,
  createCodeSnippetItem,
  createAssetItem,
  createImageItem,
  createVideoItem,
  createLottieItem,
  createAudioItem,
  createColorItem,
  createFontItem,
  createSupportingImageItem,
  createSupportingVideoItem,
  createSupportingLottieItem,
  createImageAsset,
  createVideoAsset,
  createLottieAsset,
  createAudioAsset,
  createColorAsset,
  createLinkAsset,
  createFontAsset,
  createDocumentAsset,
  createSketchAsset,
  createState,
  createPortal,
};
